import { HTTPInstance } from "@http/http-instance";
import { FeatureFlag } from "@http/constants";
import { createPayment } from "@/api/paymentAPI";
import { placeOrderAndPayMock } from "./OrdersAPI.mock";
import {
  OrderPlacedPaymentFailedError,
  type CreateOrderPayload,
  type OrderPaymentDetails,
  type PlaceOrderResult,
} from "./OrdersAPI.model";

/**
 * Place the order and take payment for it.
 *
 * These are one operation on purpose. The guest experience is "pay and enjoy",
 * a single tap, but the server cannot take a payment before an order exists:
 * createPayment resolves the order from `table.currentOrderId` and rejects
 * with "No active order for this table" otherwise. So the real sequence is
 * order then pay, hidden behind this seam so no screen has to know.
 *
 * The order half decides for itself whether to open a bill or append to the
 * table's open one, and broadcasts the result on the table's socket, which is
 * why nothing here has to touch the local bill state.
 */
const placeOrderAndPayReal = async (
  restaurantId: string,
  payload: CreateOrderPayload,
  payment: OrderPaymentDetails,
): Promise<PlaceOrderResult> => {
  const { data } = await HTTPInstance.post<{ orderId?: string }>(
    `/public/restaurants/${restaurantId}/orders`,
    payload,
  );

  const orderId = data?.orderId ?? "";

  // A cart of nothing but rewards costs nothing, and the payment endpoint
  // rejects a zero amount (`@IsPositive()` on CreatePaymentDto.amount). There
  // is nothing to charge, so there is nothing to call.
  if (payment.amountGrosze + payment.tipGrosze === 0) {
    return { orderId, orderCode: "", paidAmountGrosze: 0 };
  }

  // From here the dishes exist on the table's bill whatever happens next, so a
  // failure has to be reported as such rather than as "nothing happened".
  try {
    const { orderCode } = await createPayment(payload.tableId, {
      method: payment.method,
      amount: payment.amountGrosze,
      ...(payment.tipGrosze > 0 && { tipAmount: payment.tipGrosze }),
    });

    return {
      orderId,
      orderCode,
      paidAmountGrosze: payment.amountGrosze + payment.tipGrosze,
    };
  } catch (error) {
    throw new OrderPlacedPaymentFailedError(orderId, error);
  }
};

export const placeOrderAndPay = (
  restaurantId: string,
  payload: CreateOrderPayload,
  payment: OrderPaymentDetails,
): Promise<PlaceOrderResult> =>
  FeatureFlag.USE_MOCK_ORDERS
    ? placeOrderAndPayMock(restaurantId, payload, payment)
    : placeOrderAndPayReal(restaurantId, payload, payment);
